/**
 * KAN-754: диалог ручной категории: создать (название, родитель
 * необязателен) или переименовать. Только для владельца.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { useCatalogCategoryOptions, useCatalogMutations } from '@/hooks/useCatalog';
import { CATEGORY_NAME_MAX, serverFieldErrors, validateCategoryName } from '@/lib/catalogForm';
import type { CatalogCategory } from '@/types/catalog';
import { FormField } from './FormField';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Категория для переименования; нет: создание. */
  category?: CatalogCategory | null;
  /** Родитель новой категории по умолчанию (открытая в каталоге). */
  defaultParentId?: string | null;
  onSaved?: (category: CatalogCategory) => void;
}

interface Errors {
  name?: string;
  parentId?: string;
}

export function CategoryFormDialog({ open, onOpenChange, category, defaultParentId, onSaved }: Props): JSX.Element {
  const { createCategory, updateCategory } = useCatalogMutations();
  const options = useCatalogCategoryOptions();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setParentId(category ? '' : (defaultParentId ?? ''));
    setErrors({});
    setFormError(null);
  }, [open, category, defaultParentId]);

  const pending = createCategory.isPending || updateCategory.isPending;

  const submit = async (): Promise<void> => {
    const nameError = validateCategoryName(name);
    setErrors(nameError ? { name: nameError } : {});
    setFormError(null);
    if (nameError) return;
    try {
      const saved = category
        ? await updateCategory.mutateAsync({ id: category.id, body: { name: name.trim() } })
        : await createCategory.mutateAsync({ name: name.trim(), parent_id: parentId || null });
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      const server = serverFieldErrors(err, 'Не удалось сохранить категорию');
      setErrors(server.fields);
      setFormError(server.form);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-form-dialog">
        <DialogHeader>
          <DialogTitle>{category ? 'Переименовать категорию' : 'Новая категория'}</DialogTitle>
          <DialogDescription>Категория добавляется вручную, фид её не меняет и не удаляет.</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <FormField id="category-form-name" label="Название *" error={errors.name}>
            <Input
              id="category-form-name"
              value={name}
              maxLength={CATEGORY_NAME_MAX}
              autoComplete="off"
              disabled={pending}
              aria-invalid={errors.name ? true : undefined}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
          {!category && (
            <FormField id="category-form-parent" label="Родительская категория" error={errors.parentId}>
              <NativeSelect
                id="category-form-parent"
                options={[{ value: '', label: 'Без родителя (верхний уровень)' }, ...options]}
                value={parentId}
                disabled={pending}
                onChange={(e) => setParentId(e.target.value)}
              />
            </FormField>
          )}
          {formError && (
            <p className="text-xs text-red-700" data-testid="category-form-error">
              {formError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              Отмена
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
